import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useStatistics } from '../contexts/SettingsContext';

const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const statistics = useStatistics();

  const stats = [
    { number: `${statistics.companies_formed?.toLocaleString()}+`, label: t('about.stats.companiesFormedLabel', 'Companies Formed'), icon: '🏢' },
    { number: `${statistics.countries_served}+`, label: t('about.stats.countriesServedLabel', 'Countries Served'), icon: '🌍' },
    { number: `${statistics.years_experience}+`, label: t('about.stats.yearsExperienceLabel', 'Years Experience'), icon: '⭐' },
    { number: statistics.support_availability, label: t('about.stats.customerSupportLabel', 'Customer Support'), icon: '🛟' },
  ];

  const whyChooseUs = [
    {
      title: t('about.whyChooseUs.streamlinedProcess.title'),
      description: t('about.whyChooseUs.streamlinedProcess.description'),
      icon: '⚡',
      features: [
        t('about.whyChooseUs.streamlinedProcess.features.digitalDocumentation'),
        t('about.whyChooseUs.streamlinedProcess.features.automatedFiling'),
        t('about.whyChooseUs.streamlinedProcess.features.realTimeUpdates')
      ]
    },
    {
      title: t('about.whyChooseUs.globalExpertise.title'),
      description: t('about.whyChooseUs.globalExpertise.description'),
      icon: '🌍',
      features: [
        t('about.whyChooseUs.globalExpertise.features.multiCountrySupport'),
        t('about.whyChooseUs.globalExpertise.features.localCompliance'),
        t('about.whyChooseUs.globalExpertise.features.expertGuidance')
      ]
    },
    {
      title: t('about.whyChooseUs.dedicatedSupport.title'),
      description: t('about.whyChooseUs.dedicatedSupport.description'),
      icon: '🤝',
      features: [
        t('about.whyChooseUs.dedicatedSupport.features.24_7Availability'),
        t('about.whyChooseUs.dedicatedSupport.features.personalConsultant'),
        t('about.whyChooseUs.dedicatedSupport.features.ongoingAssistance')
      ]
    },
  ];

  const values = [
    {
      title: t('about.transparency'),
      description: t('about.transparencyDesc'),
      icon: '💎',
    },
    {
      title: t('about.excellence'),
      description: t('about.excellenceDesc'),
      icon: '🏆',
    },
    {
      title: t('about.innovation'),
      description: t('about.innovationDesc'),
      icon: '🚀',
    },
    {
      title: t('about.globalReach'),
      description: t('about.globalReachDesc'),
      icon: '🌐',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('about.title')}
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('about.mission')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.missionText')}
              </p>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.missionText2')}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2 bg-primary-50 px-4 py-2 rounded-full">
                  <span className="text-primary-600">✓</span>
                  <span className="text-primary-700 font-medium">{t('about.expertGuidance')}</span>
                </div>
                <div className="flex items-center space-x-2 bg-primary-50 px-4 py-2 rounded-full">
                  <span className="text-primary-600">✓</span>
                  <span className="text-primary-700 font-medium">{t('about.globalExpertise')}</span>
                </div>
                <div className="flex items-center space-x-2 bg-primary-50 px-4 py-2 rounded-full">
                  <span className="text-primary-600">✓</span>
                  <span className="text-primary-700 font-medium">{t('about.fastProcessing')}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/about_us_imgae.jpg"
                  alt={t('about.officeAlt')}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-black/70 backdrop-blur-sm p-4 rounded-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{statistics.years_experience}+</div>
                  <div className="text-white/90 text-sm">{t('about.yearsExp')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.trustedBy')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.numbersSpeak')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.values')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.valuesSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.whyChooseUs.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.whyChooseUs.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>
                <div className="space-y-3">
                  {item.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('about.cta')}
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              {t('about.ctaSubtitle')}
            </p>
            <motion.a
              href="/contact"
              className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-50 transition-colors duration-200 shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('about.getStartedToday')}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;